import { useCallback, useEffect, useMemo, useState } from 'react';
import * as templatesApi from '../api/templates';
import type { Template } from '../api/templates';
import { useSidebar } from '../components/AppShell';
import { useMe } from '../hooks/useMe';
import { CreateTemplateModal } from '../components/CreateTemplateModal';
import { EditTemplateModal } from '../components/EditTemplateModal';
import { SettingsToast } from '../components/SettingsToast';

/**
 * ADR 0010 /templates list + CRUD + launch. Any authenticated user sees
 * the templates in every group they're a member of. Launch instantiates
 * a task immediately; the scheduled scanner is deferred.
 */
export function TemplatesView() {
  const { toggle } = useSidebar();
  const { me } = useMe();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const t = await templatesApi.listTemplates();
      setTemplates(t);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const grouped = useMemo(() => {
    const byGroup = new Map<string, Template[]>();
    for (const t of templates) {
      const arr = byGroup.get(t.groupId) ?? [];
      arr.push(t);
      byGroup.set(t.groupId, arr);
    }
    return Array.from(byGroup.entries())
      .map(([groupId, ts]) => ({
        groupId,
        groupDisplayName: ts[0]?.groupDisplayName ?? groupId,
        templates: [...ts].sort((a, b) => a.title.localeCompare(b.title)),
      }))
      .sort((a, b) => a.groupDisplayName.localeCompare(b.groupDisplayName));
  }, [templates]);

  const onLaunch = useCallback(
    async (t: Template) => {
      setBusyId(t.templateId);
      try {
        await templatesApi.launchTemplate(t.groupId, t.templateId);
        setToast(`Launched "${t.title}" — task created`);
        await refresh();
      } catch (err) {
        alert((err as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [refresh]
  );

  const onDelete = useCallback(
    async (t: Template) => {
      if (!window.confirm(`Delete template "${t.title}"? This cannot be undone.`)) {
        return;
      }
      setBusyId(t.templateId);
      try {
        await templatesApi.deleteTemplate(t.groupId, t.templateId);
        setToast(`Deleted "${t.title}"`);
        await refresh();
      } catch (err) {
        alert((err as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [refresh]
  );

  const onToggleActive = useCallback(
    async (t: Template) => {
      setBusyId(t.templateId);
      try {
        await templatesApi.updateTemplate(t.groupId, t.templateId, {
          active: !t.active,
        });
        setToast(`${!t.active ? 'Activated' : 'Paused'} "${t.title}"`);
        await refresh();
      } catch (err) {
        alert((err as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [refresh]
  );

  return (
    <div className="scope-view templates-view">
      <header className="scope-view-header settings-header">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={toggle}
          aria-label="Open menu"
          title="Menu"
        >
          <span aria-hidden="true">☰</span>
        </button>
        <h1>Templates</h1>
        <div className="view-header-tools">
          <button
            type="button"
            className="primary"
            onClick={() => setCreateOpen(true)}
            disabled={!me || me.groups.length === 0}
          >
            + New template
          </button>
        </div>
      </header>

      <div className="settings-body">
        {loading ? (
          <div className="settings-loading muted">Loading templates…</div>
        ) : error ? (
          <div className="settings-error">{error}</div>
        ) : templates.length === 0 ? (
          <div className="settings-empty muted">
            No templates yet — create your first.
          </div>
        ) : (
          <div className="templates-list">
            {grouped.map((section) => (
              <section key={section.groupId} className="templates-group">
                <h2 className="templates-group-heading">
                  {section.groupDisplayName}
                </h2>
                <ul className="templates-group-list">
                  {section.templates.map((t) => {
                    const busy = busyId === t.templateId;
                    const schedule =
                      t.scheduleDays.length === 0
                        ? 'Manual only'
                        : t.scheduleDays.map(formatDay).join(', ');
                    return (
                      <li key={t.templateId} className="template-card">
                        <div className="template-card-main">
                          <div className="template-card-title">
                            <strong>{t.title}</strong>
                            {!t.active && (
                              <span className="template-chip is-paused">
                                Paused
                              </span>
                            )}
                          </div>
                          <div className="template-card-meta muted small">
                            <span>{schedule}</span>
                            <span>·</span>
                            <span>
                              {t.checklistItems.length}{' '}
                              {t.checklistItems.length === 1 ? 'item' : 'items'}
                            </span>
                            {t.lastGeneratedDate && (
                              <>
                                <span>·</span>
                                <span>Last run {t.lastGeneratedDate}</span>
                              </>
                            )}
                          </div>
                          {t.description && (
                            <p className="template-card-description muted">
                              {t.description}
                            </p>
                          )}
                        </div>
                        <div className="template-card-actions">
                          <button
                            type="button"
                            className="primary"
                            disabled={busy}
                            onClick={() => void onLaunch(t)}
                          >
                            {busy ? '…' : 'Launch now'}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setEditing(t)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void onToggleActive(t)}
                          >
                            {t.active ? 'Pause' : 'Activate'}
                          </button>
                          <button
                            type="button"
                            className="danger"
                            disabled={busy}
                            onClick={() => void onDelete(t)}
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      <CreateTemplateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onDone={async (msg) => {
          setCreateOpen(false);
          setToast(msg);
          await refresh();
        }}
      />
      <EditTemplateModal
        open={!!editing}
        template={editing}
        onClose={() => setEditing(null)}
        onDone={async (msg) => {
          setEditing(null);
          setToast(msg);
          await refresh();
        }}
      />
      <SettingsToast message={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

function formatDay(n: number): string {
  if (n >= 11 && n <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
